import React, { useEffect, useMemo, useState } from "react";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/shared/EmptyState";
import { QRCodeModal } from "@/components/customer/QRCodeModal";
import { TicketCheck, CheckCircle2, XCircle } from "lucide-react";

const STATUS_TABS = [
    { key: "CLAIMED", label: "Active" },
    { key: "REDEEMED", label: "Used" },
    { key: "EXPIRED", label: "Expired" },
];

export default function CustomerCoupons() {
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("CLAIMED");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get("/me/coupons?limit=200")
            .then((r) => setClaims(r.data.claims || []))
            .catch((e) => toast.error(errorMessage(e)))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => claims.filter((c) => c.status === statusFilter), [claims, statusFilter]);

    return (
        <div data-testid="customer-coupons">
            <h1 className="section-title text-2xl">My Coupons</h1>
            <p className="mt-1 text-sm text-[var(--text-muted)]">Claimed coupons ready to show at the counter.</p>

            <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mt-4">
                <TabsList data-testid="coupons-status-tabs">
                    {STATUS_TABS.map((s) => (
                        <TabsTrigger key={s.key} value={s.key} data-testid={`coupons-tab-${s.key}`}>
                            {s.label} ({claims.filter((c) => c.status === s.key).length})
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            <div className="mt-4">
                {loading ? (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <EmptyState title="No coupons here" hint="Claim an offer with QR to save it." icon={TicketCheck} />
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="coupons-grid">
                        {filtered.map((c) => {
                            const isActive = c.status === "CLAIMED";
                            const isExpired = c.status === "EXPIRED";
                            const stripe = isActive ? "var(--brand-orange)" : isExpired ? "#F43F5E" : "var(--text-muted)";
                            return (
                                <div
                                    key={c.code}
                                    onClick={() => isActive && setSelected(c)}
                                    data-testid={`coupon-row-${c.code}`}
                                    className={`card-soft relative overflow-hidden !p-0 ${isActive ? "cursor-pointer hover:border-[var(--brand-orange)]" : ""} ${isExpired ? "opacity-70" : ""}`}
                                >
                                    <span aria-hidden className="absolute inset-y-0 left-0 w-1.5" style={{ background: stripe }} />
                                    <div className="flex items-stretch pl-1.5">
                                        {c.post_poster_image_url && (
                                            <img src={publicAssetUrl(c.post_poster_image_url)} alt="" className="h-24 w-24 shrink-0 object-cover" />
                                        )}
                                        <div className="flex flex-1 flex-col justify-center gap-1 p-3">
                                            {c.business_name && <div className="truncate font-heading text-xs font-semibold uppercase text-[var(--brand-orange)]">{c.business_name}</div>}
                                            <div className="truncate font-heading text-sm font-bold text-[var(--text-primary)]">{c.post_title}</div>
                                            <div className="mt-1 font-mono text-xs tracking-wider text-[var(--text-muted)]">{c.code}</div>
                                            <div className="mt-1 flex items-center gap-2 text-[10px]">
                                                <StatusPill status={c.status} />
                                                {c.discount_snapshot?.value != null && (
                                                    <span className="savings-pill !text-xs">
                                                        {c.discount_snapshot.type === "FLAT" ? `₹${c.discount_snapshot.value} OFF` : `Save ${c.discount_snapshot.value}%`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <QRCodeModal open={!!selected} onOpenChange={(v) => !v && setSelected(null)} claim={selected} />
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        CLAIMED: { bg: "bg-emerald-100 text-emerald-800", icon: <TicketCheck size={10} className="mr-1" />, label: "Active" },
        REDEEMED: { bg: "bg-neutral-200 text-neutral-700", icon: <CheckCircle2 size={10} className="mr-1" />, label: "Redeemed" },
        EXPIRED: { bg: "bg-rose-100 text-rose-700", icon: <XCircle size={10} className="mr-1" />, label: "Expired" },
    };
    const cfg = map[status] || map.CLAIMED;
    return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${cfg.bg}`}>{cfg.icon}{cfg.label}</span>;
}
